import { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../app/components/AppButton';
import { useAppSelector } from '../../../../app/store/hooks';
import { paths, rolePath } from '../../../../app/routes/paths';
import { useAppRole } from '../../../../app/routes/useRolePath';
import { LESSON_CURRENCIES, type LessonOfferingDraft } from '../../application/types';
import {
  useGetMyLessonOfferingsQuery,
  useUpdateLessonOfferingMutation,
} from '../../infrastructure/api/lessonOfferings.api';
import { LessonForm } from '../components/LessonForm';
import { LessonPackageSection } from '../components/LessonPackageSection';
import { offeringPayload } from './offeringPayload';
import './lessons.scss';

function draftFrom(offering: {
  title: string;
  durationMinutes: number;
  price: number;
  currency: string;
}): LessonOfferingDraft {
  const currency = LESSON_CURRENCIES.includes(offering.currency as LessonOfferingDraft['currency'])
    ? (offering.currency as LessonOfferingDraft['currency'])
    : 'USD';
  return {
    title: offering.title,
    durationMinutes: String(offering.durationMinutes),
    price: String(offering.price),
    currency,
  };
}

export function LessonEditPage() {
  const { t } = useTranslation();
  const role = useAppRole();
  const { state } = useLocation();
  const { lessonOfferingId = '' } = useParams();
  const openPackage =
    !!state &&
    typeof state === 'object' &&
    'addPackage' in state &&
    Boolean((state as { addPackage?: boolean }).addPackage);
  const offeringId = Number(lessonOfferingId);
  const accessToken = useAppSelector((state) => state.authorization.accessToken);
  const { data: offerings = [], isLoading } = useGetMyLessonOfferingsQuery(undefined, {
    skip: !accessToken,
  });
  const offering = offerings.find((item) => item.id === offeringId) ?? null;
  const [draft, setDraft] = useState<LessonOfferingDraft | null>(null);
  const [updateOffering, { isLoading: isSaving }] = useUpdateLessonOfferingMutation();

  useEffect(() => {
    if (!offering || draft) return;
    setDraft(draftFrom(offering));
  }, [offering, draft]);

  const payload = draft ? offeringPayload(draft) : null;

  const onSave = async () => {
    if (!payload || !offering) return;
    try {
      await updateOffering({ id: offering.id, ...payload }).unwrap();
    } catch {
      // The mutation shows the error toast.
    }
  };

  if (!isLoading && !offering) {
    return (
      <div className="mentor-lessons">
        <p>{t('lesson.not_found')}</p>
        <Link to={rolePath(role, paths.lessons)}>{t('lesson.my_lessons')}</Link>
      </div>
    );
  }

  return (
    <div className="mentor-lessons">
      <h1>{t('lesson.edit_lesson')}</h1>
      {draft ? <LessonForm value={draft} onChange={setDraft} /> : null}
      {offering ? (
        <LessonPackageSection offering={offering} initialOpen={openPackage} />
      ) : null}
      <div className="mentor-lessons__actions">
        <Link to={rolePath(role, paths.lessons)}>{t('lesson.cancel')}</Link>
        <Button type="button" variant="primary" onClick={onSave} disabled={!payload || isSaving}>
          {t('lesson.save_changes')}
        </Button>
      </div>
    </div>
  );
}

export default LessonEditPage;
